import { useState, useEffect, useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import {
  ReportedStolenFilter,
  LockStateFilter,
  CriticalBatteryFilter,
  ServiceModeFilter,
  DeviceFilters,
} from "./types"

export const useFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterReportedStolen, setFilterReportedStolen] = useState<ReportedStolenFilter>("any")
  const [filterLockState, setFilterLockState] = useState<LockStateFilter>("any")
  const [filterCriticalBattery, setFilterCriticalBattery] = useState<CriticalBatteryFilter>("any")
  const [filterServiceMode, setFilterServiceMode] = useState<ServiceModeFilter>("any")

  // Load filters from URL params
  const searchParamsString = searchParams.toString()
  useEffect(() => {
    const rs = searchParams.get("reported_stolen")
    setFilterReportedStolen(rs === "true" ? "true" : rs === "false" ? "false" : "any")

    const ls = searchParams.get("lock_state") as "open" | "locked" | null
    setFilterLockState(ls === "open" || ls === "locked" ? ls : "any")

    const critical = searchParams.get("critical_battery")
    setFilterCriticalBattery(critical === "true" ? "true" : "any")

    const status = searchParams.get("servicebooking_status")
    const endBefore = searchParams.get("servicebooking_end_before")

    if (status === "none") {
      setFilterServiceMode("no_service")
    } else if (status === "active") {
      setFilterServiceMode("active")
    } else if (endBefore && !status) {
      setFilterServiceMode("expires_90d")
    } else {
      setFilterServiceMode("any")
    }
  }, [searchParams, searchParamsString])

  const updateParam = useCallback(
    (key: string, value?: string | null) => {
      const next = new URLSearchParams(searchParams)
      if (value === undefined || value === null || value === "") next.delete(key)
      else next.set(key, value)
      setSearchParams(next)
    },
    [searchParams, setSearchParams],
  )

  const hasActiveFilters = useMemo(
    () =>
      filterReportedStolen !== "any" ||
      filterLockState !== "any" ||
      filterCriticalBattery === "true" ||
      filterServiceMode !== "any",
    [filterReportedStolen, filterLockState, filterCriticalBattery, filterServiceMode],
  )

  const resetFilters = useCallback(() => {
    setFilterReportedStolen("any")
    setFilterLockState("any")
    setFilterCriticalBattery("any")
    setFilterServiceMode("any")

    const next = new URLSearchParams(searchParams)
    next.delete("reported_stolen")
    next.delete("lock_state")
    next.delete("critical_battery")
    next.delete("servicebooking_status")
    next.delete("servicebooking_end_before")
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const buildFilters = useCallback((): DeviceFilters => {
    const reportedStolenParam = searchParams.get("reported_stolen")
    const reportedStolen = reportedStolenParam === null ? undefined : reportedStolenParam === "true"
    const servicebookingStatus =
      (searchParams.get("servicebooking_status") as "active" | "none" | null) || undefined
    const servicebookingEndBefore = searchParams.get("servicebooking_end_before") || undefined
    const criticalBatteryParam = searchParams.get("critical_battery")
    const batteryBelow = criticalBatteryParam === "true" ? 21 : undefined
    const lockState = (searchParams.get("lock_state") as "open" | "locked" | null) || undefined

    return {
      reported_stolen: reportedStolen,
      servicebooking_status: servicebookingStatus,
      servicebooking_end_before: servicebookingEndBefore,
      battery_below: batteryBelow,
      lock_state: lockState,
    }
  }, [searchParams])

  return {
    filterReportedStolen,
    setFilterReportedStolen,
    filterLockState,
    setFilterLockState,
    filterCriticalBattery,
    setFilterCriticalBattery,
    filterServiceMode,
    setFilterServiceMode,
    updateParam,
    hasActiveFilters,
    resetFilters,
    buildFilters,
    searchParams,
    setSearchParams,
  }
}
